import React, { useEffect, useMemo, useState } from 'react';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useAuth } from '../../../contexts/AuthContext';
import { inviteMerchant } from '../../../services/merchants/adminMerchantApi';
import type { AdminCompanyDirectoryRow } from '../../../models/adminCompany';

export interface CompanyDirectoryTableProps {
  rows: AdminCompanyDirectoryRow[];
  companyId: string;
  onResent: () => void;
}

/** Rows shown per page. */
const PAGE_SIZE = 10;

function formatDateTime(value: string): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function formatLocation(row: AdminCompanyDirectoryRow): string {
  const parts = [row.city, row.state, row.country].filter((p) => p.trim() !== '');
  return parts.length > 0 ? parts.join(', ') : '—';
}

function statusLabel(row: AdminCompanyDirectoryRow): string {
  if (row.invitationId) {
    return row.invitationStatus || 'Pending';
  }
  return row.isActive ? 'Active' : 'Pending';
}

function statusPillClass(row: AdminCompanyDirectoryRow): string {
  const label = statusLabel(row).toLowerCase();
  return label === 'active' || label === 'pending' ? 'bo-status-active' : 'bo-status-inactive';
}

/** A company's full merchant + invitation directory, with a resend action per invitation row. */
export function CompanyDirectoryTable({ rows, companyId, onResent }: CompanyDirectoryTableProps): React.ReactElement {
  const { acquireAccessToken } = useAuth();
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  // Keep the current page within range when the row set shrinks (e.g. after a reload).
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageRows = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);

  const handleResend = async (row: AdminCompanyDirectoryRow): Promise<void> => {
    if (!row.invitationId) {
      return;
    }
    setError(null);
    setResendingId(row.invitationId);
    try {
      await inviteMerchant(
        {
          email: row.email,
          suggestedMerchantName: row.name,
          companyId,
        },
        acquireAccessToken
      );
      onResent();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resend invitation.');
    } finally {
      setResendingId(null);
    }
  };

  if (rows.length === 0) {
    return <p className="bo-muted">No merchants or invitations for this company yet.</p>;
  }

  return (
    <div>
      {error ? <ErrorAlert message={error} /> : null}
      <div className="bo-admin-table-wrap">
        <table className="bo-admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Location</th>
              <th>Status</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const key = row.merchantId ?? row.invitationId ?? row.email;
              const canResend = Boolean(row.invitationId) && row.invitationStatus !== 'Accepted';
              const isPending = row.invitationStatus === 'Pending';
              const isResending = resendingId === row.invitationId;
              return (
                <tr key={key}>
                  <td>{row.name || '—'}</td>
                  <td>{row.email || '—'}</td>
                  <td>{row.phone || '—'}</td>
                  <td>{formatLocation(row)}</td>
                  <td>
                    <span className={`bo-status-pill ${statusPillClass(row)}`}>{statusLabel(row)}</span>
                  </td>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td>
                    {canResend ? (
                      <div className="bo-actions">
                        <button
                          type="button"
                          className="bo-btn bo-btn-secondary"
                          onClick={() => void handleResend(row)}
                          disabled={isResending || isPending}
                          title={isPending ? 'Still pending — resend is available once it expires.' : undefined}
                        >
                          {isResending ? 'Resending…' : 'Resend'}
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > PAGE_SIZE ? (
        <div className="bo-admin-pagination">
          <span className="bo-muted">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div className="bo-admin-pagination-controls">
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="bo-muted">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
